"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getECFromEvent = exports.getLeakFromEvent = exports.getHumidityFromEvent = exports.parseSeerValue = exports.eerToLabel = exports.eerToStatus = exports.waterQualityThresholdToLabel = exports.waterQualityThresholdToStatus = exports.getWaterQualityThreshold = exports.pmToStatus = exports.vocToStatus = exports.co2ToStatus = exports.iaqToLabel = exports.iaqToStatus = exports.filterHealthToLabel = exports.filterHealthToStatus = exports.parseSensorEvent = exports.worstStatus = exports.STATUS_PRIORITY = void 0;
const AirQuality_1 = require("../types/AirQuality");
const SensorAlert_1 = require("../types/SensorAlert");
const WaterQualityStatus_1 = require("../types/water/WaterQualityStatus");
// ── Shared status logic ────────────────────────────────────────────────────
/**
 * Ranking for `worstStatus`. The load-bearing part is `Offline` sitting BELOW
 * `Poor`: a coverage gap must never outrank a live hazard, and it must never
 * be outranked by nothing at all. `Calibrating` stays tied to `Good` — the
 * aggregation hooks rely on a sensor waiting for its first BSEC calibration
 * being unable to drag the household card down.
 *
 * This is the ONLY ranking table. `useEnergyAggregation` used to carry a
 * private copy; it now imports this one.
 */
exports.STATUS_PRIORITY = {
    [SensorAlert_1.SensorCardStatus.Good]: 0,
    [SensorAlert_1.SensorCardStatus.Calibrating]: 0,
    [SensorAlert_1.SensorCardStatus.Unknown]: 1,
    [SensorAlert_1.SensorCardStatus.Offline]: 2,
    [SensorAlert_1.SensorCardStatus.Poor]: 3,
    [SensorAlert_1.SensorCardStatus.Error]: 4,
};
function worstStatus(a, b) {
    return exports.STATUS_PRIORITY[a] >= exports.STATUS_PRIORITY[b] ? a : b;
}
exports.worstStatus = worstStatus;
/** Parse GraphQL event: values/metadata may come as JSON strings */
function parseSensorEvent(raw) {
    const parsed = { ...raw };
    if (raw.values && typeof raw.values === 'string') {
        try {
            parsed.values = JSON.parse(raw.values);
        }
        catch { /* keep original */ }
    }
    if (raw.metadata && typeof raw.metadata === 'string') {
        try {
            parsed.parsedMetadata = JSON.parse(raw.metadata);
        }
        catch { /* ignore */ }
    }
    return parsed;
}
exports.parseSensorEvent = parseSensorEvent;
// ── Air quality classification ─────────────────────────────────────────────
function filterHealthToStatus(healthPct) {
    if (healthPct === undefined || healthPct === null)
        return SensorAlert_1.SensorCardStatus.Good;
    if (healthPct > 50)
        return SensorAlert_1.SensorCardStatus.Good;
    if (healthPct > 20)
        return SensorAlert_1.SensorCardStatus.Poor;
    return SensorAlert_1.SensorCardStatus.Error;
}
exports.filterHealthToStatus = filterHealthToStatus;
function filterHealthToLabel(healthPct) {
    if (healthPct === undefined || healthPct === null)
        return 'Unknown';
    if (healthPct > 50)
        return 'Good';
    if (healthPct > 20)
        return 'Low';
    return 'Replace';
}
exports.filterHealthToLabel = filterHealthToLabel;
function iaqToStatus(iaq) {
    if (iaq < 0)
        return SensorAlert_1.SensorCardStatus.Error;
    if (iaq <= 100)
        return SensorAlert_1.SensorCardStatus.Good;
    if (iaq <= 200)
        return SensorAlert_1.SensorCardStatus.Poor;
    return SensorAlert_1.SensorCardStatus.Error;
}
exports.iaqToStatus = iaqToStatus;
function iaqToLabel(iaq) {
    if (iaq < 0)
        return 'Sensor Fault';
    if (iaq <= 50)
        return 'Great';
    if (iaq <= 100)
        return 'Good';
    if (iaq <= 150)
        return 'Fair';
    if (iaq <= 200)
        return 'Poor';
    if (iaq <= 300)
        return 'Very Poor';
    return 'Severe';
}
exports.iaqToLabel = iaqToLabel;
// BSEC-derived metrics beyond IAQ (co2Equivalent, breathVoc) and PM2.5 (pm).
// Same 3-tier shape as iaqToStatus: Good at/below the "good" threshold, Poor
// between "good" and "moderate", Error above "moderate" — this matches the
// detail-card widgets (CO2LevelCard/VOCLevelCard/AirQualityStatusCard), where
// their own "poor" and "unhealthy" tiers both already render as the red/error
// gradient, so collapsing everything above "moderate" into Error is correct.
function co2ToStatus(co2Equivalent) {
    if (co2Equivalent === undefined || co2Equivalent === null || isNaN(co2Equivalent)) {
        return SensorAlert_1.SensorCardStatus.Good;
    }
    const { co2 } = AirQuality_1.AirQualityThresholds;
    if (co2Equivalent <= co2.good)
        return SensorAlert_1.SensorCardStatus.Good;
    if (co2Equivalent <= co2.moderate)
        return SensorAlert_1.SensorCardStatus.Poor;
    return SensorAlert_1.SensorCardStatus.Error;
}
exports.co2ToStatus = co2ToStatus;
function vocToStatus(breathVoc) {
    if (breathVoc === undefined || breathVoc === null || isNaN(breathVoc)) {
        return SensorAlert_1.SensorCardStatus.Good;
    }
    const { voc } = AirQuality_1.AirQualityThresholds;
    if (breathVoc <= voc.good)
        return SensorAlert_1.SensorCardStatus.Good;
    if (breathVoc <= voc.moderate)
        return SensorAlert_1.SensorCardStatus.Poor;
    return SensorAlert_1.SensorCardStatus.Error;
}
exports.vocToStatus = vocToStatus;
function pmToStatus(pm) {
    if (pm === undefined || pm === null || isNaN(pm)) {
        return SensorAlert_1.SensorCardStatus.Good;
    }
    const { pm25 } = AirQuality_1.AirQualityThresholds;
    if (pm <= pm25.good)
        return SensorAlert_1.SensorCardStatus.Good;
    if (pm <= pm25.moderate)
        return SensorAlert_1.SensorCardStatus.Poor;
    return SensorAlert_1.SensorCardStatus.Error;
}
exports.pmToStatus = pmToStatus;
// ── Water quality classification ───────────────────────────────────────────
function getWaterQualityThreshold(ecValue) {
    // Bands are TDS ppm; convert from EC at the standard 0.6 factor.
    const tds = ecValue * 0.6;
    if (tds <= WaterQualityStatus_1.WaterQualityThreshold.excellent)
        return WaterQualityStatus_1.WaterQualityThreshold.excellent;
    if (tds <= WaterQualityStatus_1.WaterQualityThreshold.good)
        return WaterQualityStatus_1.WaterQualityThreshold.good;
    if (tds <= WaterQualityStatus_1.WaterQualityThreshold.acceptable)
        return WaterQualityStatus_1.WaterQualityThreshold.acceptable;
    if (tds <= WaterQualityStatus_1.WaterQualityThreshold.hard)
        return WaterQualityStatus_1.WaterQualityThreshold.hard;
    return WaterQualityStatus_1.WaterQualityThreshold.concerning;
}
exports.getWaterQualityThreshold = getWaterQualityThreshold;
function waterQualityThresholdToStatus(threshold) {
    if (!threshold)
        return SensorAlert_1.SensorCardStatus.Good;
    switch (threshold) {
        case WaterQualityStatus_1.WaterQualityThreshold.excellent:
        case WaterQualityStatus_1.WaterQualityThreshold.good:
        case WaterQualityStatus_1.WaterQualityThreshold.acceptable:
            return SensorAlert_1.SensorCardStatus.Good;
        case WaterQualityStatus_1.WaterQualityThreshold.hard:
            return SensorAlert_1.SensorCardStatus.Poor;
        case WaterQualityStatus_1.WaterQualityThreshold.concerning:
            return SensorAlert_1.SensorCardStatus.Error;
        default:
            return SensorAlert_1.SensorCardStatus.Good;
    }
}
exports.waterQualityThresholdToStatus = waterQualityThresholdToStatus;
function waterQualityThresholdToLabel(threshold) {
    if (!threshold)
        return 'Unknown';
    if (threshold <= WaterQualityStatus_1.WaterQualityThreshold.excellent)
        return 'Excellent';
    if (threshold <= WaterQualityStatus_1.WaterQualityThreshold.good)
        return 'Good';
    if (threshold <= WaterQualityStatus_1.WaterQualityThreshold.acceptable)
        return 'Acceptable';
    if (threshold <= WaterQualityStatus_1.WaterQualityThreshold.hard)
        return 'Hard';
    return 'Concerning';
}
exports.waterQualityThresholdToLabel = waterQualityThresholdToLabel;
// ── HVAC / EER classification ──────────────────────────────────────────────
function eerToStatus(eer) {
    if (eer >= 8)
        return SensorAlert_1.SensorCardStatus.Good;
    if (eer >= 4)
        return SensorAlert_1.SensorCardStatus.Poor;
    return SensorAlert_1.SensorCardStatus.Error;
}
exports.eerToStatus = eerToStatus;
function eerToLabel(eer) {
    if (eer >= 12)
        return 'Great';
    if (eer >= 8)
        return 'Good';
    if (eer >= 4)
        return 'Fair';
    return 'Poor';
}
exports.eerToLabel = eerToLabel;
function parseSeerValue(data) {
    if (!data)
        return 0;
    // Fallback chain: instant -> lastMinute -> runningHour
    const instant = data.instantSeerRating ?? 0;
    if (instant > 0)
        return instant;
    const lastMin = parseFloat(data.lastMinuteSeerRating?.seerValue ?? '0');
    if (lastMin > 0)
        return lastMin;
    const runningHour = parseFloat(data.runningHourSeerRating?.seerValue ?? '0');
    if (runningHour > 0)
        return runningHour;
    return 0;
}
exports.parseSeerValue = parseSeerValue;
// ── Water sensor data extraction ───────────────────────────────────────────
/** Extract humidity from a leak detector event, handling multiple data formats */
function getHumidityFromEvent(event) {
    if (!event)
        return null;
    // Direct values access (most common after parseSensorEvent)
    const vals = event.values;
    if (vals?.humidity !== undefined)
        return Number(vals.humidity);
    // Fallback: parse value string (comma-separated: temp,humidity,leak,battery)
    if (event.value && typeof event.value === 'string') {
        const parts = event.value.split(',');
        if (parts.length >= 2) {
            const h = parseFloat(parts[1]);
            if (!isNaN(h))
                return h;
        }
    }
    return null;
}
exports.getHumidityFromEvent = getHumidityFromEvent;
/** Extract leak status from event */
function getLeakFromEvent(event) {
    if (!event)
        return false;
    const vals = event.values;
    if (vals?.leak !== undefined)
        return Number(vals.leak) === 1;
    if (event.value && typeof event.value === 'string') {
        const parts = event.value.split(',');
        if (parts.length >= 3)
            return parseFloat(parts[2]) === 1;
    }
    return false;
}
exports.getLeakFromEvent = getLeakFromEvent;
/** Extract EC value from a water quality event */
function getECFromEvent(event) {
    if (!event)
        return 0;
    const vals = event.values;
    if (vals?.ec !== undefined)
        return Number(vals.ec);
    // Fallback: value string (ec,battery)
    if (event.value && typeof event.value === 'string') {
        const parts = event.value.split(',');
        if (parts.length >= 1) {
            const ec = parseFloat(parts[0]);
            if (!isNaN(ec))
                return ec;
        }
    }
    return 0;
}
exports.getECFromEvent = getECFromEvent;
